// English messages for Customer communication (Klantberichten). Spread into en.ts.
// Strings that existed before translation are kept word for word, so the screen
// tests (which run in English) describe the same screen.

import type { Vars } from './en.ts';

export const messagesEn = {
  // --- the screen -------------------------------------------------------------
  'msg.title': 'Customer communication',
  'msg.intro': 'Ask customers for missing documents.',
  'msg.noTransport': 'Emails are not sent yet. Approved messages are kept.',
  'msg.empty.title': 'No messages yet',
  'msg.empty.line': 'Open a bank transaction without an invoice to ask the customer something',
  'msg.empty.action': 'Go to Bank matching',
  'msg.nothingToAsk': 'Nothing to ask about.',
  'msg.created': 'The request is open. Write the message, then approve it — nothing goes out until you do.',
  'msg.createdExisting': 'This customer has already been asked about that. Opened the existing request rather than a second one.',
  'msg.selectAdministration': 'Select an administration first.',
  'msg.contactAdded': 'Recorded, and unverified. Nothing can be sent to it until somebody confirms the address is right.',
  'msg.contactVerified': 'Verified, with your account of how, against that exact address.',

  // --- the list ---------------------------------------------------------------
  'msg.list.title': 'Requests',
  'msg.list.sub': 'Everything this office has asked for, newest first',
  'msg.list.reading': 'Reading the requests…',
  'msg.noSubject': 'No message written yet',
  'msg.addressUnverified': ' · address not verified',
  'msg.select.title': 'Select a request',
  'msg.select.text': 'Its message, who it is addressed to, and everything that came back appear here.',

  // --- a new request ----------------------------------------------------------
  'msg.new.title': 'Ask about this',
  'msg.new.bankLine': 'Bank transaction',
  'msg.new.document': 'Document',
  'msg.new.roleCannot': (v: Vars) => `Your role (${v['role']}) may not create a customer request.`,
  'msg.new.noContacts':
    'This administration has nobody recorded to write to yet. Add a contact below, then come back — a request has to be addressed to somebody the office has recorded.',
  'msg.new.ask': 'Ask',
  'msg.new.choose': 'Choose a contact…',
  'msg.new.notVerified': ' — not verified',
  'msg.new.unverifiedNote':
    'You can write to an unverified contact now and approve it later. Sending needs somebody to have confirmed the address first.',
  'msg.new.notNow': 'Not now',
  'msg.new.open': 'Open a request',

  // --- one request ------------------------------------------------------------
  'msg.detail.reading': 'Reading this request…',
  'msg.detail.to': (v: Vars) => `To ${v['name']}`,
  'msg.step.written': 'Written',
  'msg.step.approved': 'Approved',
  'msg.step.sending': 'Sending',
  'msg.step.sent': 'Sent',
  'msg.detail.unverified':
    'Nobody has confirmed this address. It can be written to and not sent to — confirm it on the contacts card below, and the request becomes approvable.',
  'msg.detail.addressMoved':
    'The contact was corrected after this message was written, so the approval would be for an address that is no longer current. Write the message again to pick up the new one.',
  'msg.detail.operation': 'Send operation:',
  'msg.saved': 'Saved as a new revision. Nothing has been approved and nothing has been sent.',
  'msg.writeFirst': 'Write the message first.',
  'msg.approved': (v: Vars) =>
    `Approved and queued as operation ${v['id']}. Watch its state above — in this deployment it will report that nothing was sent.`,
  'msg.attached':
    'Attached. The request is waiting on somebody to check that this is actually the document that was asked for — nothing has been booked.',
  'msg.replies.title': 'What came back',
  'msg.replies.noNote': 'Attached without a note',
  'msg.replies.noProposal': ' · nothing prepared from it yet',
  'msg.replies.proposal': (v: Vars) => ` · a proposal is ${v['status']}`,
  'msg.replies.prepared': 'prepared',
  'msg.replies.open': 'Open the document',
  'msg.events.title': 'Mail events',
  'msg.events.thread': (v: Vars) => ` · thread ${v['ref']}`,
  'msg.revisions.title': 'Every version of this message',
  'msg.revisions.none': 'Nothing has been written yet.',
  'msg.revisions.subject': 'Subject',
  'msg.revisions.to': 'To',
  'msg.revisions.written': 'Written',
  'msg.revisions.approved': 'Approved',
  'msg.revisions.note':
    'Older versions are kept rather than replaced. An approval is bound to one of them, so “what exactly did we approve, and to which address?” stays answerable.',

  // --- why approval is blocked -----------------------------------------------
  'msg.blocked.role': (v: Vars) => `Your role (${v['role']}) may not approve a message for sending.`,
  'msg.blocked.unverified': 'Nobody has confirmed this address. Verify the contact below first.',
  'msg.blocked.moved': 'The contact was corrected after this message was written. Write it again.',

  // --- the message editor -----------------------------------------------------
  'msg.draft.roleCannot': (v: Vars) => `Your role (${v['role']}) may read this request and not write or approve its message.`,
  'msg.draft.title': 'The message',
  'msg.draft.subject': 'Subject',
  'msg.draft.body': 'Message',
  'msg.draft.goesTo': (v: Vars) =>
    `Goes to ${v['address']}, which is the address recorded for this contact. To change it, correct the contact — that makes it unverified again.`,
  'msg.draft.save': 'Save this version',
  'msg.draft.approve': 'Approve and queue the send',
  'msg.draft.approveTitle': 'Approve this exact message and queue the send',

  // --- a reply ----------------------------------------------------------------
  'msg.reply.nothing': 'Nothing has been asked yet, so there is nothing that could be a reply to it.',
  'msg.reply.title': 'Attach what came back',
  'msg.reply.note':
    'The document has to have arrived already, through this administration’s intake. Attaching it records that somebody believes it answers this request; it does not approve a booking, and it is not proof the right document arrived.',
  'msg.reply.document': 'Document',
  'msg.reply.documentPlaceholder': 'The arrival’s id',
  'msg.reply.thread': 'Mail thread (optional)',
  'msg.reply.why': 'Why you think this is the reply',
  'msg.reply.attach': 'Attach it',

  // --- contacts ---------------------------------------------------------------
  'msg.contacts.title': 'Contacts',
  'msg.contacts.pick': 'Administration',
  'msg.contacts.choose': 'Choose an administration',
  'msg.contacts.pickLine': 'Pick an administration to see its contacts.',
  'msg.contacts.reading': 'Reading the contacts…',
  'msg.contacts.none': 'Nobody recorded yet for this administration.',
  'msg.contacts.version': (v: Vars) => `version ${v['version']}`,
  'msg.contacts.verifiedHow': (v: Vars) => `verified — ${v['method']}`,
  'msg.contacts.noAccount': 'no account given',
  'msg.contacts.notVerifiedLower': 'not verified',
  'msg.contacts.howLabel': 'How did you confirm this address?',
  'msg.contacts.howPlaceholder': 'Called the number on the invoice',
  'msg.contacts.record': 'Record the verification',
  'msg.contacts.verified': 'Verified',
  'msg.contacts.notVerified': 'Not verified',
  'msg.contacts.verify': 'Verify',
  'msg.contacts.close': 'Close',
  'msg.contacts.addTitle': 'Record a contact',
  'msg.contacts.address': 'Email address',
  'msg.contacts.name': 'Name (optional)',
  'msg.contacts.addNote':
    'A new contact is never verified. Somebody has to confirm the address is right before anything can be sent to it, and that is a separate act with its own record.',
  'msg.contacts.add': 'Record it',
  'msg.contacts.roleCannot': (v: Vars) => `Your role (${v['role']}) may read these contacts and not change them.`,

  // --- errors -----------------------------------------------------------------
  'msg.error.read': 'This could not be read.',
  'msg.error.transport': 'The service could not be reached.',
  'msg.error.absent': 'This is not available to your session.',
  'msg.error.forbiddenRead': 'Your role does not allow reading this.',
  'msg.error.action': 'The request could not be completed.',
  'msg.error.stale': 'This changed since the screen was loaded, so nothing was recorded. Reload and look at what is there now.',
  'msg.error.forbidden': 'Your role does not allow this action.',
  'msg.error.gone': 'That is no longer there — somebody may have dealt with it first.',
  'msg.error.unverified': 'Nothing was sent: nobody has confirmed that address. Verify the contact and approve again.',
  'msg.error.refused': (v: Vars) => `The request was refused: ${v['fields']}.`,
  'msg.error.validation': 'validation failed',
} as const;
